import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getDebtorSummary } from "api/debtor.service";
import { navigateTo } from "common/navigation";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import React, { useEffect, useMemo, useState } from "react";
import { DebtorSummaries } from "types/model.types";

const AdminDebtorLists: React.FC = () => {
  const [debtors, setDebtors] = useState<DebtorSummaries>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const result = await safeApiCall(getDebtorSummary());
        if (mounted && result) setDebtors(result);
      } catch (err) {
        setError("Failed to fetch overdue accounts.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();
    return debtors
      .filter((b) => {
        const name = b.name?.toLowerCase() ?? "";

        const matchesSearch = [
          b.name,
          b.primaryAddress,
          b.attn1,
          b.attn2,
          b.email,
        ].some((field) => {
          return (field ?? "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .includes(normalizedSearch);
        });

        return matchesSearch;
      })
      .sort((a, b) => {
        const nameA = a.name?.toLowerCase() ?? "";
        const nameB = b.name?.toLowerCase() ?? "";
        return nameA.localeCompare(nameB);
      });
  }, [debtors, searchTerm, statusFilter]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  const rows = paginatedItems.map((debtor) => {
    return (
      <Table.Tr key={debtor.id}>
        <Table.Td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {debtor.name}
        </Table.Td>
        <Table.Td>
          <Text component="div">
            {(debtor.primaryAddress ?? "").split(/\r?\n/).map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </Text>
        </Table.Td>
        <Table.Td>{debtor.phone}</Table.Td>
        <Table.Td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {debtor.attn1}
        </Table.Td>
        <Table.Td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {debtor.attn2}
        </Table.Td>
        <Table.Td>{debtor.email}</Table.Td>
        <Table.Td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          <Badge
            color={debtor.active ? "green" : "red"}
            size="sm"
            variant="light"
            style={{ maxWidth: "100%", width: "80px", whiteSpace: "normal" }}
          >
            {debtor.active ? "Active" : "Inactive"}
          </Badge>
        </Table.Td>
        <Table.Td></Table.Td>
      </Table.Tr>
    );
  });
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
  };

  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Debtor Accounts</Title>
        <Button
          onClick={() => {
            navigateTo(`/print/debtor-lists?s=${searchTerm ?? ""}`);
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : error ? (
        <Alert color="red">{error}</Alert>
      ) : (
        <>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Text size="sm" c="dimmed">
              Showing 30 items per page
            </Text>
            <Pagination
              total={Math.ceil(filteredItems.length / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>

          <TextInput
            placeholder="Search by any title"
            mb="md"
            leftSection={<IconSearch size={16} stroke={1.5} />}
            value={searchTerm}
            onChange={handleSearchChange}
          />

          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Delivery Address</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th>Attention 1</Table.Th>
                <Table.Th>Attention 2</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(filteredItems.length / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
    </Stack>
  );
};

export default AdminDebtorLists;
